import { useEffect, useRef, useState } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query'

export const DEFAULT_SKELETON_ITEMS = [
  { loading: true as const, id: 'loading-0' },
  { loading: true as const, id: 'loading-1' },
]

interface UseContactListOptions<T> {
  contacts: T[] | undefined
  isFetchingNextPage: boolean
  hasLoadedMore: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<T[], Error>>
  setHasLoadedMore: React.Dispatch<React.SetStateAction<boolean>>
}

export const useContactList = <T>({
  contacts,
  isFetchingNextPage,
  hasLoadedMore,
  fetchNextPage,
  setHasLoadedMore,
}: UseContactListOptions<T>) => {
  type ListItem = T | { loading: true; id: string }

  const listRef = useRef<HTMLUListElement>(null)
  const [contactList, setContactList] = useState<ListItem[]>([])

  useEffect(() => {
    if (isFetchingNextPage && hasLoadedMore) {
      setContactList(prevItems => [...prevItems, ...DEFAULT_SKELETON_ITEMS])
    } else if (contacts) {
      setContactList(contacts as ListItem[])
    }
  }, [contacts, isFetchingNextPage, hasLoadedMore])

  useEffect(() => {
    if (listRef?.current && hasLoadedMore) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [contactList, hasLoadedMore])

  const handleOnLoadMore = () => {
    fetchNextPage()
    setHasLoadedMore(true)
  }

  return { listRef, contactList, handleOnLoadMore }
}
